// A small Markdown reader for the chat log.
//
// Not spec-complete and not trying to be. It covers what a Claude Code reply
// actually contains — fenced code, headings, lists, tables, quotes, and inline
// code/emphasis/links — and leaves anything it doesn't recognise as the text it
// already was. Hand-rolled rather than a dependency for two reasons: thirty
// transitive packages is a lot to add to a one-box prototype, and the streaming
// case wants a parser that reads an unclosed fence as a code block still
// arriving rather than as broken input.
//
// The output is a data tree, never markup. Nothing here produces HTML, so a
// reply cannot inject any — see app/markdown.tsx for the renderer.

/** Column alignment from a table's delimiter row; null when unspecified. */
export type Align = 'left' | 'center' | 'right' | null;

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'del'; children: Inline[] }
  | { kind: 'link'; href: string; children: Inline[] }
  /** A single newline inside a paragraph, kept as a break rather than folded to
   *  a space. Whether one is emitted depends on where the source came from —
   *  see `parseMarkdown`. */
  | { kind: 'br' };

export type Block =
  | { kind: 'para'; inline: Inline[] }
  | { kind: 'heading'; level: number; inline: Inline[] }
  /** `open` means the fence never closed — i.e. still being streamed. */
  | { kind: 'code'; lang: string; text: string; open: boolean }
  | { kind: 'list'; ordered: boolean; start: number; items: Block[][] }
  | { kind: 'quote'; blocks: Block[] }
  | { kind: 'rule' }
  | { kind: 'table'; head: Inline[][]; rows: Inline[][][]; align: Align[] };

const FENCE = /^ {0,3}(`{3,}|~{3,})[ \t]*([^\s`]*)/;
const HEADING = /^ {0,3}(#{1,6})[ \t]+(.*)$/;
const RULE = /^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/;
const QUOTE = /^ {0,3}>[ \t]?/;
const ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/;
const DELIM_ROW = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

/** Punctuation that a backslash may escape. */
const ESCAPABLE = /[\\`*_{}[\]()#+\-.!~>|]/;

/** Tabs count as four columns, which is all indentation is measured in here. */
function indentOf(prefix: string): number {
  let n = 0;
  for (const c of prefix) n += c === '\t' ? 4 - (n % 4) : 1;
  return n;
}

/** Drop up to `width` columns of leading whitespace. */
function dedent(line: string, width: number): string {
  let i = 0;
  let col = 0;
  while (i < line.length && col < width) {
    if (line[i] === ' ') col += 1;
    else if (line[i] === '\t') col += 4 - (col % 4);
    else break;
    i += 1;
  }
  return line.slice(i);
}

function isBlockStart(line: string): boolean {
  return (
    !line.trim() ||
    FENCE.test(line) ||
    HEADING.test(line) ||
    RULE.test(line) ||
    QUOTE.test(line) ||
    ITEM.test(line)
  );
}

/**
 * Parse Markdown source into blocks. Safe to call on a partial document.
 *
 * `breaks` decides what a single newline inside a paragraph means, and the
 * answer is not a matter of taste — it depends on who wrote the newline.
 *
 * In a chat log a person pressed Return, and dropping that would rewrite what
 * they said. In a file on disk the newline is almost always an editor wrapping
 * at some column the reader cannot see, and honouring it snaps sentences in half
 * at arbitrary points — "shipped as a Rust library / crate." is what that looks
 * like on a phone. CommonMark calls the second one a soft break and renders it
 * as a space, which is the behaviour a wiki file wants.
 *
 * Defaults to `true` because the chat is the older caller and the riskier one to
 * get wrong: folding a break someone typed loses information, where honouring
 * one they did not type only looks bad.
 */
export function parseMarkdown(src: string, breaks = true): Block[] {
  return parseBlocks(src.replace(/\r\n?/g, '\n').split('\n'), breaks);
}

function parseBlocks(lines: string[], breaks: boolean): Block[] {
  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const [, marker, lang] = fence;
      const close = new RegExp(`^ {0,3}${marker[0]}{${marker.length},}[ \t]*$`);
      const body: string[] = [];
      i += 1;
      let closed = false;
      while (i < lines.length) {
        if (close.test(lines[i])) {
          closed = true;
          i += 1;
          break;
        }
        body.push(lines[i]);
        i += 1;
      }
      out.push({ kind: 'code', lang, text: body.join('\n'), open: !closed });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const text = heading[2].replace(/[ \t]+#+[ \t]*$/, '').trim();
      out.push({ kind: 'heading', level: heading[1].length, inline: parseInline(text, breaks) });
      i += 1;
      continue;
    }

    if (RULE.test(line)) {
      out.push({ kind: 'rule' });
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        body.push(lines[i].replace(QUOTE, ''));
        i += 1;
      }
      out.push({ kind: 'quote', blocks: parseBlocks(body, breaks) });
      continue;
    }

    if (ITEM.test(line)) {
      const [list, next] = parseList(lines, i, breaks);
      out.push(list);
      i = next;
      continue;
    }

    // A table is a header row plus a delimiter row; without the second line
    // this is just a paragraph that happens to contain pipes.
    if (line.includes('|') && i + 1 < lines.length && DELIM_ROW.test(lines[i + 1])) {
      const [table, next] = parseTable(lines, i, breaks);
      out.push(table);
      i = next;
      continue;
    }

    // Paragraph: everything up to a blank line or the start of another block.
    const body: string[] = [line];
    i += 1;
    while (i < lines.length && !isBlockStart(lines[i])) {
      // A delimiter row would have made the previous line a table header, and a
      // paragraph must not swallow it.
      if (lines[i].includes('|') && i + 1 < lines.length && DELIM_ROW.test(lines[i + 1])) break;
      body.push(lines[i]);
      i += 1;
    }
    out.push({ kind: 'para', inline: parseInline(body.join('\n').trim(), breaks) });
  }

  return out;
}

/**
 * One list, from the marker at `start` to the first line that isn't part of it.
 *
 * An item owns its own continuation lines: anything indented to the item's
 * content column, and — as Markdown allows — an unindented line that simply
 * continues the paragraph it began. Nesting needs no special case, because item
 * content is parsed as blocks in its own right.
 */
function parseList(lines: string[], start: number, breaks: boolean): [Block, number] {
  const first = ITEM.exec(lines[start])!;
  const ordered = /\d/.test(first[2]);
  const marker = indentOf(first[1]);
  const items: Block[][] = [];

  let i = start;
  let own: string[] = [];
  const close = () => {
    if (own.length) items.push(parseBlocks(own, breaks));
    own = [];
  };

  while (i < lines.length) {
    const item = ITEM.exec(lines[i]);

    // A marker at this list's own indentation starts the next item. One further
    // in belongs to the item as nested content; one further out ends the list.
    if (item && indentOf(item[1]) === marker && /\d/.test(item[2]) === ordered) {
      close();
      own.push(item[3]);
      i += 1;
    } else if (item && indentOf(item[1]) > marker && own.length) {
      own.push(dedent(lines[i], marker + item[2].length + 1));
      i += 1;
    } else if (!lines[i].trim()) {
      // A blank line only continues the list if indented content follows it.
      const after = lines[i + 1];
      if (after === undefined) break;
      if (!after.trim()) break;
      if (indentOf(/^[ \t]*/.exec(after)![0]) <= marker && !ITEM.test(after)) break;
      own.push('');
      i += 1;
    } else if (indentOf(/^[ \t]*/.exec(lines[i])![0]) > marker) {
      own.push(dedent(lines[i], marker + first[2].length + 1));
      i += 1;
    } else if (own.length && own[own.length - 1].trim() && !isBlockStart(lines[i])) {
      own.push(lines[i]); // lazy continuation of this item's paragraph
      i += 1;
    } else {
      break;
    }
  }

  close();
  const startNo = ordered ? Number(first[2].slice(0, -1)) : 1;
  return [{ kind: 'list', ordered, start: startNo, items }, i];
}

function parseTable(lines: string[], start: number, breaks: boolean): [Block, number] {
  const head = cells(lines[start]).map(cell => parseInline(cell, breaks));
  const align: Align[] = cells(lines[start + 1]).map(spec => {
    const left = spec.startsWith(':');
    const right = spec.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    return null;
  });

  const rows: Inline[][][] = [];
  let i = start + 2;
  // Body rows run until a blank line or something that stops looking like one.
  while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
    rows.push(cells(lines[i]).map(cell => parseInline(cell, breaks)));
    i += 1;
  }
  return [{ kind: 'table', head, rows, align }, i];
}

/** Split a table row on unescaped pipes, dropping the optional outer ones. */
function cells(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  for (let i = 0; i < line.length; i += 1) {
    if (line[i] === '\\' && line[i + 1] === '|') {
      cur += '|';
      i += 1;
    } else if (line[i] === '|') {
      out.push(cur);
      cur = '';
    } else {
      cur += line[i];
    }
  }
  out.push(cur);
  if (!out[0].trim()) out.shift();
  if (out.length && !out[out.length - 1].trim()) out.pop();
  return out.map(c => c.trim());
}

/* ── Inline ────────────────────────────────────────────────────────────────── */

// Longest first, so `***` is not read as `**` followed by a stray `*`.
//
// `__bold__` is deliberately absent. CommonMark bolds it, which also bolds
// `__init__` and `__main__` — and in a log about code, dunder names in prose are
// far more common than anyone reaching for underscores to mean bold when `**`
// is right there.
const DELIMS: { delim: string; kind: 'strong' | 'em' | 'del' }[] = [
  { delim: '***', kind: 'strong' }, // em nested inside, handled below
  { delim: '**', kind: 'strong' },
  { delim: '~~', kind: 'del' },
  { delim: '*', kind: 'em' },
  { delim: '_', kind: 'em' },
];

const URL_AT = /^(?:https?:\/\/|www\.)[^\s<>()[\]"']+/;

/** Schemes we will put in an href. Anything else renders as plain text. */
function safeHref(raw: string): string | null {
  const href = raw.trim().replace(/^<|>$/g, '');
  if (!href) return null;
  if (/^(?:https?:|mailto:|tel:)/i.test(href)) return href;
  if (/^www\./i.test(href)) return `https://${href}`;
  if (/^[#/]/.test(href) || /^\.{1,2}\//.test(href)) return href;
  // Relative-looking with no scheme is fine; a scheme we don't know is not.
  return /^[a-z][a-z0-9+.-]*:/i.test(href) ? null : href;
}

/**
 * Parse one line's worth (or a paragraph's worth) of inline Markdown.
 *
 * `breaks` is the same choice `parseMarkdown` documents: keep a single newline
 * as a break, or fold it to a space the way CommonMark's soft break does.
 */
export function parseInline(src: string, breaks = true): Inline[] {
  const out: Inline[] = [];
  let text = '';
  const flush = () => {
    if (text) out.push({ kind: 'text', text });
    text = '';
  };

  let i = 0;
  while (i < src.length) {
    const c = src[i];

    if (c === '\\' && i + 1 < src.length && ESCAPABLE.test(src[i + 1])) {
      text += src[i + 1];
      i += 2;
      continue;
    }

    if (c === '\n') {
      if (breaks) {
        flush();
        out.push({ kind: 'br' });
      } else {
        // A soft break. Folded to a space rather than dropped, or the last word
        // of one line would run into the first of the next.
        text += ' ';
      }
      i += 1;
      continue;
    }

    if (c === '`') {
      const span = readCode(src, i);
      if (span) {
        flush();
        out.push(span.node);
        i = span.end;
        continue;
      }
    }

    if (c === '[' || (c === '!' && src[i + 1] === '[')) {
      const link = readLink(src, c === '!' ? i + 1 : i, breaks);
      if (link) {
        flush();
        out.push(link.node);
        i = link.end;
        continue;
      }
    }

    if (c === '*' || c === '_' || c === '~') {
      const emph = readEmphasis(src, i, breaks);
      if (emph) {
        flush();
        out.push(emph.node);
        i = emph.end;
        continue;
      }
    }

    if ((c === 'h' || c === 'w') && (i === 0 || /[\s(]/.test(src[i - 1]))) {
      const url = URL_AT.exec(src.slice(i));
      if (url) {
        // Sentence punctuation at the end belongs to the sentence, not the URL.
        const raw = url[0].replace(/[.,;:!?]+$/, '');
        const href = safeHref(raw);
        if (href) {
          flush();
          out.push({ kind: 'link', href, children: [{ kind: 'text', text: raw }] });
          i += raw.length;
          continue;
        }
      }
    }

    text += c;
    i += 1;
  }

  flush();
  return out;
}

function readCode(src: string, at: number): { node: Inline; end: number } | null {
  const run = /^`+/.exec(src.slice(at))![0];
  let from = at + run.length;
  for (;;) {
    const close = src.indexOf(run, from);
    if (close === -1) return null; // unterminated: it is literal backticks
    // A longer run of backticks is not this span's closer.
    if (src[close + run.length] === '`') {
      from = close + run.length;
      while (src[from] === '`') from += 1;
      continue;
    }
    let inner = src.slice(at + run.length, close).replace(/\n/g, ' ');
    if (inner.length > 2 && inner.startsWith(' ') && inner.endsWith(' ') && inner.trim()) {
      inner = inner.slice(1, -1);
    }
    return { node: { kind: 'code', text: inner }, end: close + run.length };
  }
}

/** `[label](href)`, with a bracketed label that may itself contain brackets. */
function readLink(src: string, at: number, breaks: boolean): { node: Inline; end: number } | null {
  let depth = 0;
  let close = -1;
  for (let i = at; i < src.length; i += 1) {
    if (src[i] === '\\') {
      i += 1;
      continue;
    }
    if (src[i] === '`') {
      const span = readCode(src, i);
      if (span) i = span.end - 1;
      continue;
    }
    if (src[i] === '[') depth += 1;
    else if (src[i] === ']') {
      depth -= 1;
      if (depth === 0) {
        close = i;
        break;
      }
    }
  }
  if (close === -1 || src[close + 1] !== '(') return null;

  let depthP = 0;
  let end = -1;
  for (let i = close + 1; i < src.length; i += 1) {
    if (src[i] === '\\') {
      i += 1;
      continue;
    }
    if (src[i] === '(') depthP += 1;
    else if (src[i] === ')') {
      depthP -= 1;
      if (depthP === 0) {
        end = i;
        break;
      }
    } else if (src[i] === '\n' && src[i + 1] === '\n') break;
  }
  if (end === -1) return null;

  // A title in quotes after the URL is not rendered, only skipped.
  const target = src.slice(close + 2, end).trim().replace(/\s+["'(][\s\S]*$/, '');
  const href = safeHref(target);
  if (!href) return null;
  return {
    node: { kind: 'link', href, children: parseInline(src.slice(at + 1, close), breaks) },
    end: end + 1,
  };
}

/**
 * Emphasis at `at`, or null if this run of `*`/`_`/`~` doesn't open one.
 *
 * The rule that earns its keep is the one for `_`: mid-word underscores are
 * identifiers (`base_turns`, `__init__`), not italics, so an underscore only
 * opens emphasis at a word boundary.
 */
function readEmphasis(src: string, at: number, breaks: boolean): { node: Inline; end: number } | null {
  for (const { delim, kind } of DELIMS) {
    if (!src.startsWith(delim, at)) continue;

    const after = src[at + delim.length];
    if (after === undefined || /\s/.test(after)) continue;
    // A longer run than any delimiter here means something we don't handle
    // (`****`), and half-reading it is worse than leaving it alone.
    if (after === delim[0]) continue;
    // Underscores only ever delimit at a word boundary: `a_b_c` and `__init__`
    // are identifiers, not emphasis.
    if (delim[0] === '_' && at > 0 && /[\w_]/.test(src[at - 1])) continue;

    const close = findClose(src, at + delim.length, delim);
    if (close === -1) continue;

    const inner = src.slice(at + delim.length, close);
    const end = close + delim.length;
    if (delim === '***') {
      return { node: { kind: 'strong', children: [{ kind: 'em', children: parseInline(inner, breaks) }] }, end };
    }
    return { node: { kind, children: parseInline(inner, breaks) }, end };
  }
  return null;
}

/** Index of the delimiter that closes this emphasis, or -1. */
function findClose(src: string, from: number, delim: string): number {
  for (let i = from; i < src.length; i += 1) {
    if (src[i] === '\\') {
      i += 1;
      continue;
    }
    if (src[i] === '`') {
      const span = readCode(src, i);
      if (span) {
        i = span.end - 1;
        continue;
      }
    }
    // A blank line ends the paragraph, and emphasis cannot span one.
    if (src[i] === '\n' && src[i + 1] === '\n') return -1;
    if (!src.startsWith(delim, i)) continue;
    // Closing runs sit tight against their content, and `_` must also land on a
    // word boundary so `a_b_c` stays as typed.
    if (/\s/.test(src[i - 1])) continue;
    if (delim[0] === '_' && (/[\w_]/.test(src[i + delim.length] ?? '') || src[i - 1] === '_')) {
      continue;
    }
    if (i === from) continue; // empty, e.g. `****`
    return i;
  }
  return -1;
}
